import { useEffect, useState, useCallback } from 'react';
import {
  RecommendPanel,
  Result as RecommendJob,
} from '@doda/common/src/interfaces/api/recommendPanel';
import { postRecommend } from '@doda/common/src/services/postRecommend';
import { RecommendGrid } from 'components/RecommendGrid';
import { useAppDispatch, useAppSelector } from '@doda/pc/src/redux/hooks';
import { adobeAnalyticsScriptsSelector } from '@doda/common/src/redux/features/analytics';
import { tl } from '@doda/common/src/utils/adobeAnalyticsUtil';
import {
  DODA_SITE_RECOMMENDTYPE,
  RecommendType,
} from '@doda/common/src/constants/recommendType';
import { shuffleLoginRecommends } from '@doda/common/src/logics/shuffleLoginRecommends';
import {
  isTargetRecommendType,
  markTop4AsHighlyRecommended,
} from '@doda/common/src/logics/markTop4AsHighlyRecommended';
import {
  JOB_DETAIL_TOP4_AS_HIGHLY_RECOMMENDED_AB_TEST_GROUP,
  MARK_TOP4_AS_HIGHLY_RECOMMENDED_AB_TEST_GROUP,
} from '@doda/common/src/constants/markTop4AsHighlyRecommend';
import { jobDetailHighlyRecommendTestPatternSelector } from '../../redux/slices/jobDetailHighlyRecommendABTest';
import styles from './index.module.scss';
import {
  measureClickRecommendFavorite,
  measureClickRecommendJobCardAdobe,
  measureJobDetailRecommendABTestAllocation,
  measureHighScoreJobClick,
} from '../../logics/analytics';
import { useTab } from '../TabArea/hooks/useTab';
import { Tab } from '../../types/tab';
import { useRecommendVisibleAnalytics } from './hooks/useRecommendVisibleAnalytics';
import { JobListForRecommend } from '../../../JobHistoryList/logics/generateJobListForRecommend/generateJobListForRecommend';
import {
  JobSearchDetailViaCanMailType,
  JobSearchDetailFromAgentMessageType,
} from '../../types';
import { RECOMMENDABTESTGROUP } from '../../constants';

/**
 * RecommendSectionのprops
 */
interface Props {
  jid: string;
  initialTab: Tab;
  wcnsId: string;
  jobSearchDetailViaCanMailType: JobSearchDetailViaCanMailType;
  jobSearchDetailFromAgentMessageType: JobSearchDetailFromAgentMessageType;
}

/**
 * 求人詳細のレコメンドMAX3種表示セクション
 * @param jid 求人ID
 */
export const RecommendSection = ({
  jid,
  initialTab,
  wcnsId,
  jobSearchDetailViaCanMailType,
  jobSearchDetailFromAgentMessageType,
}: Props) => {
  const dispatch = useAppDispatch();
  const isAdobeScript = useAppSelector(adobeAnalyticsScriptsSelector);
  const jobDetailHighlyRecommendTestPattern = useAppSelector(
    jobDetailHighlyRecommendTestPatternSelector
  );
  const [recommendList, setRecommendList] = useState<RecommendPanel[]>([]);
  const { currentTabName } = useTab({ initialTab, jid, wcnsId });

  const isCanMailRecommend =
    jobSearchDetailViaCanMailType === RECOMMENDABTESTGROUP.TEST;
  const isFromAgentRecommend =
    jobSearchDetailFromAgentMessageType === RECOMMENDABTESTGROUP.TEST;
  const isHighlyRecommended =
    jobDetailHighlyRecommendTestPattern ===
      JOB_DETAIL_TOP4_AS_HIGHLY_RECOMMENDED_AB_TEST_GROUP.TEST1 ||
    jobDetailHighlyRecommendTestPattern ===
      JOB_DETAIL_TOP4_AS_HIGHLY_RECOMMENDED_AB_TEST_GROUP.TEST2;

  const createHandleClickRecommend =
    (effectName: string) =>
    (recommendJob: RecommendJob | JobListForRecommend, index: number) => {
      // DODASITE-23658: Track high-score clicks
      if ((recommendJob as RecommendJob).isHighlyRecommended) {
        measureHighScoreJobClick(recommendJob.recommendType as RecommendType);
      }

      measureClickRecommendJobCardAdobe(
        isAdobeScript,
        recommendJob.recommendType,
        effectName,
        dispatch,
        String(index),
        currentTabName
      );
    };

  const handleClickFavorite = (
    recommendJob: RecommendJob | JobListForRecommend,
    index: number
  ) => {
    // DODASITE-23658: Track high-score clicks
    if ((recommendJob as RecommendJob).isHighlyRecommended) {
      measureHighScoreJobClick(recommendJob.recommendType as RecommendType);
    }

    measureClickRecommendFavorite(
      isAdobeScript,
      recommendJob.recommendType,
      dispatch,
      String(index),
      currentTabName,
      recommendJob.itemNo
    );
  };

  const { refs } = useRecommendVisibleAnalytics({
    recommendList,
    currentTabName,
  });

  const highlightAndShuffleRecommend = useCallback(
    (panel: RecommendPanel) => {
      if (
        !panel ||
        !isTargetRecommendType(panel.recommendType as RecommendType, [
          DODA_SITE_RECOMMENDTYPE.AWS_RECOMMEND_NOT_ETSURAN,
          DODA_SITE_RECOMMENDTYPE.AWS_RECOMMEND_OUBO,
        ])
      ) {
        return panel;
      }

      const abTestPattern = isHighlyRecommended
        ? MARK_TOP4_AS_HIGHLY_RECOMMENDED_AB_TEST_GROUP.TEST
        : MARK_TOP4_AS_HIGHLY_RECOMMENDED_AB_TEST_GROUP.FAILED;

      const markedResults = markTop4AsHighlyRecommended(
        panel.results,
        true,
        panel.recommendType as RecommendType,
        abTestPattern,
        [
          DODA_SITE_RECOMMENDTYPE.AWS_RECOMMEND_NOT_ETSURAN,
          DODA_SITE_RECOMMENDTYPE.AWS_RECOMMEND_OUBO,
        ]
      );
      const shuffledResults = shuffleLoginRecommends(markedResults);

      return {
        ...panel,
        results: shuffledResults,
      };
    },
    [isHighlyRecommended]
  );

  useEffect(() => {
    if (!jid) return;
    const getJobDetailRecommendParams = () => {
      if (isFromAgentRecommend) {
        return createJobDetailRecommendFromAgentMessageForABTest(jid);
      }
      if (isCanMailRecommend) {
        return createJobDetailRecommendCanMailForABTest(jid);
      }
      return createJobDetailRecommendCanMailForABOri(jid);
    };

    const jobDetailRecommendParams = getJobDetailRecommendParams();
    const promises = jobDetailRecommendParams.map((params) =>
      postRecommend(params)
    );
    Promise.allSettled(promises).then((responses) => {
      responses.forEach((response) => {
        // データなしの場合は非表示
        if (response.status === 'rejected' || !response.value.data) return;
        const highLightRecommend = highlightAndShuffleRecommend(
          response.value.data
        );
        setRecommendList((prevList) => [...prevList, highLightRecommend]);
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jid, isCanMailRecommend, isFromAgentRecommend]);

  useEffect(() => {
    if (
      !isAdobeScript ||
      (jobSearchDetailViaCanMailType === RECOMMENDABTESTGROUP.NA &&
        jobSearchDetailFromAgentMessageType === RECOMMENDABTESTGROUP.NA)
    )
      return;

    const getLinkName = () => {
      if (jobSearchDetailFromAgentMessageType !== RECOMMENDABTESTGROUP.NA) {
        // DODASITE-8201_本配信スカウトの求人詳細のレコメンドをスカウトに変更
        return isFromAgentRecommend
          ? 'test_shousai_plus_scout'
          : 'ori_shousai_plus_scout';
      }
      // DODASITE-8200_canメール経由の求人詳細のレコメンドをcan寄りに変更_ABテスト
      // PV計測より前に発火しないように発火を遅らせる
      return isCanMailRecommend
        ? 'test_shousai_inflowmail'
        : 'ori_shousai_inflowmail';
    };

    const linkName = getLinkName();
    setTimeout(() => {
      tl(linkName, {});
    }, 500);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdobeScript]);

  // DODASITE-23658: Track AB test allocation
  useEffect(() => {
    const hasTargetPanels = recommendList.some((panel) =>
      (
        [
          DODA_SITE_RECOMMENDTYPE.AWS_RECOMMEND_NOT_ETSURAN,
          DODA_SITE_RECOMMENDTYPE.AWS_RECOMMEND_OUBO,
        ] as RecommendType[]
      ).includes(panel.recommendType as RecommendType)
    );
    measureJobDetailRecommendABTestAllocation(
      jobDetailHighlyRecommendTestPattern,
      hasTargetPanels
    );
  }, [jobDetailHighlyRecommendTestPattern, recommendList]);

  if (!recommendList.length) return null;

  // DODASITE-23658: Check if there are highlighted jobs
  const hasHighlightedJobs = (panel: RecommendPanel) =>
    panel.results.some((result) => result.isHighlyRecommended);

  return (
    <section className={styles['jobSearchDetail-recommendSection']}>
      {recommendList.map((recommend, index) => {
        return (
          <div
            key={recommend.recommendType}
            data-recommend-type={recommend.recommendType}
            ref={refs[index]?.ref ?? null}
          >
            <RecommendGrid
              recommendList={recommend.results}
              title={recommend.title}
              recommendType={recommend.recommendType}
              bookmarkListUrl="/dcfront/bookmark/bookmarkList/"
              saveBookmarkRequestBody={{ device: 'PC' }}
              isWideCard
              onClickFavorite={handleClickFavorite}
              onClickThumbnail={createHandleClickRecommend(
                recommend.effectName
              )}
              onClickCardDetail={createHandleClickRecommend(
                recommend.effectName
              )}
              onClickSaved={undefined}
              jobDetailHighlyRecommendPattern={
                jobDetailHighlyRecommendTestPattern
              }
              description={
                hasHighlightedJobs(recommend) && isHighlyRecommended
                  ? '行動に基づくおすすめ求人の中から、さらにあなたの志向に近い求人を「特におすすめ」としています'
                  : ''
              }
            />
          </div>
        );
      })}
    </section>
  );
};

const createJobDetailRecommendParams = (jid: string) => [
  {
    // あなたが応募した求人に近い求人
    recommendType: 'AWS_Recommend_oubo',
    dispType: 'json',
    effectEventId: 'event70',
    effectPosition: 'Rekome_pc',
    effectName: 'AWS_Recommend_oubo_pc',
    conditionItems: jid,
    location: window.location.href,
  },
  {
    // 気になるリストに保存している求人
    recommendType: 'bookmark_list',
    dispType: 'json',
    effectEventId: 'event70',
    effectPosition: 'Rekome_pc',
    effectName: 'bookmark_list_pc',
    conditionItems: jid,
    location: window.location.href,
  },
  {
    // 行動履歴からおすすめの求人のうちまだ見ていない求人, あなたが閲覧した求人
    recommendType: 'AWS_Recommend_not_etsuran,view_list',
    dispType: 'json',
    effectEventId: 'event70,event70',
    effectPosition: 'Rekome_pc,Recome_pc',
    effectName: 'AWS_Recommend_not_etsuran_pc,view_list_pc',
    conditionItems: jid,
    location: window.location.href,
  },
];

/**
 * 類似レコメンド（類似順）+通常のレコメンド表示
 * DODASITE-8200_canメール経由レコメンド オリジナルパターン
 * @param jid
 */
const createJobDetailRecommendCanMailForABOri = (jid: string) => [
  {
    // 類似レコメンド（類似順）
    recommendType: 'w2v_KyujinSyousai_recommend',
    dispType: 'json',
    effectEventId: 'event70',
    effectPosition: 'Rekome_pc',
    effectName: 'w2v_KyujinSyousai_recommend',
    conditionItems: jid,
    location: window.location.href,
  },
  ...createJobDetailRecommendParams(jid),
];

/**
 * 類似レコメンド（書類通過スコア順）+通常のレコメンド表示
 * DODASITE-8201_本配信スカウトの求人詳細のレコメンドをスカウトに変更
 * @param jid
 */
const createJobDetailRecommendFromAgentMessageForABTest = (jid: string) => [
  {
    recommendType: 'plus_scout_list',
    dispType: 'json',
    effectEventId: 'event70',
    effectPosition: 'Rekome_pc',
    effectName: 'plus_scout_list',
    conditionItems: jid,
    location: window.location.href,
  },
  ...createJobDetailRecommendParams(jid),
];

/**
 * 類似レコメンド（書類通過スコア順）+通常のレコメンド表示
 * DODASITE-8200_canメール経由レコメンド テストパターン
 * @param jid
 */
const createJobDetailRecommendCanMailForABTest = (jid: string) => [
  {
    // 類似レコメンド（書類通過スコア順）
    recommendType: 'w2v_cbm_KyujinSyousai_recommend',
    dispType: 'json',
    effectEventId: 'event70',
    effectPosition: 'Rekome_pc',
    effectName: 'w2v_cbm_KyujinSyousai_recommend',
    conditionItems: jid,
    location: window.location.href,
  },
  ...createJobDetailRecommendParams(jid),
];
